/**
 * BattleEffects.ts - Visual effects system for battle animations
 * Handles attack animations, spell effects, damage numbers, and transitions
 */

import { SpriteManager } from '../SpriteManager';
import { Renderer } from '../Renderer';
import { ASSET_MANIFEST } from '../data/AssetManifest';
import { ParticleSystem } from './ParticleSystem';

export enum EffectType {
  SLASH = 'SLASH',
  FIRE = 'FIRE',
  ICE = 'ICE',
  LIGHTNING = 'LIGHTNING',
  HEAL = 'HEAL',
  CRITICAL = 'CRITICAL',
  MISS = 'MISS',
  POISON = 'POISON',
  DEATH = 'DEATH',
  DAMAGE_FLASH = 'DAMAGE_FLASH',
  DAMAGE_NUMBER = 'DAMAGE_NUMBER',
  SCREEN_SHAKE = 'SCREEN_SHAKE',
  VICTORY_POSE = 'VICTORY_POSE',
  SLEEP = 'SLEEP',
  CHARACTER_STEP = 'CHARACTER_STEP',
  ENEMY_SHAKE = 'ENEMY_SHAKE',
}

export interface BattleEffect {
  type: EffectType;
  x: number;
  y: number;
  elapsed: number;
  duration: number;
  currentFrame: number;
  data?: any; // Effect-specific data (damage amount, etc.)
  onComplete?: () => void;
}

export interface DamageNumberEffect extends BattleEffect {
  type: EffectType.DAMAGE_NUMBER;
  data: {
    value: number;
    isCrit: boolean;
    isHeal: boolean;
    startY: number;
    floatDistance: number;
  };
}

export class BattleEffectsManager {
  private spriteManager: SpriteManager;
  private activeEffects: BattleEffect[] = [];
  private particleSystem: ParticleSystem;
  private screenShakeOffset: { x: number; y: number } = { x: 0, y: 0 };

  constructor(spriteManager: SpriteManager) {
    this.spriteManager = spriteManager;
    this.particleSystem = new ParticleSystem();
  }

  /**
   * Play an effect at a position
   */
  playEffect(
    type: EffectType,
    x: number,
    y: number,
    data?: any,
    onComplete?: () => void
  ): void {
    const duration = this.getEffectDuration(type);

    const effect: BattleEffect = {
      type,
      x,
      y,
      elapsed: 0,
      duration,
      currentFrame: 0,
      data,
      onComplete,
    };

    this.activeEffects.push(effect);
  }

  /**
   * Show damage number floating up
   */
  showDamageNumber(
    x: number,
    y: number,
    value: number,
    isCrit: boolean = false,
    isHeal: boolean = false
  ): void {
    const effect: DamageNumberEffect = {
      type: EffectType.DAMAGE_NUMBER,
      x,
      y,
      elapsed: 0,
      duration: 1000, // 1 second float
      currentFrame: 0,
      data: {
        value,
        isCrit,
        isHeal,
        startY: y,
        floatDistance: 30,
      },
    };

    this.activeEffects.push(effect);
  }

  /**
   * Flash an entity white (damage indication)
   */
  flashEntity(x: number, y: number, width: number, height: number): void {
    this.playEffect(EffectType.DAMAGE_FLASH, x, y, { width, height });
    // Add hit particles
    this.particleSystem.emitHitParticles(x + width / 2, y + height / 2, 6);
  }

  /**
   * Screen shake effect for big hits
   */
  screenShake(intensity: number = 8, duration: number = 200): void {
    this.playEffect(EffectType.SCREEN_SHAKE, 0, 0, { intensity, shakeDuration: duration });
  }

  /**
   * Get screen shake offset for rendering
   */
  getScreenShakeOffset(): { x: number; y: number } {
    return this.screenShakeOffset;
  }

  /**
   * Get the step offset for a character (for attack animation)
   * Returns how many pixels the character has stepped forward
   */
  getCharacterStepOffset(characterIndex: number): number {
    const stepEffect = this.activeEffects.find(
      e => e.type === EffectType.CHARACTER_STEP && e.data?.characterIndex === characterIndex
    );

    if (!stepEffect) return 0;

    const progress = stepEffect.elapsed / stepEffect.duration;
    const forward = stepEffect.data?.forward ?? true;
    const maxStep = 20; // Max pixels to step forward

    if (forward) {
      // Step forward then return
      if (progress < 0.5) {
        return progress * 2 * maxStep; // 0 to maxStep
      } else {
        return (1 - (progress - 0.5) * 2) * maxStep; // maxStep to 0
      }
    } else {
      // Just return to position
      return (1 - progress) * maxStep;
    }
  }

  /**
   * Character step forward animation
   */
  characterStep(characterIndex: number, forward: boolean = true): void {
    this.playEffect(EffectType.CHARACTER_STEP, 0, 0, { characterIndex, forward });
  }

  /**
   * Enemy shake when hit
   */
  enemyShake(x: number, y: number): void {
    this.playEffect(EffectType.ENEMY_SHAKE, x, y, { originalX: x });
  }

  /**
   * Victory pose animation
   */
  victoryPose(): void {
    this.playEffect(EffectType.VICTORY_POSE, 0, 0, {});
    // Add celebration sparkles
    for (let i = 0; i < 30; i++) {
      setTimeout(() => {
        this.particleSystem.emitSparkles(
          50 + Math.random() * 156,
          50 + Math.random() * 100,
          3,
          '#ffff00'
        );
      }, i * 50);
    }
  }

  /**
   * Sleep status effect (Zzz)
   */
  sleepEffect(x: number, y: number): void {
    this.playEffect(EffectType.SLEEP, x, y, {});
  }

  /**
   * Update all active effects
   */
  update(deltaTime: number): void {
    // Update particle system
    this.particleSystem.update(deltaTime);

    // Update each effect
    for (let i = this.activeEffects.length - 1; i >= 0; i--) {
      const effect = this.activeEffects[i];
      if (!effect) continue;

      effect.elapsed += deltaTime;

      // Update animation frame
      const animDef = ASSET_MANIFEST.battleEffects[effect.type];
      if (animDef && animDef.frames.length > 0) {
        const frameIndex = Math.floor(
          (effect.elapsed / animDef.frameTime) % animDef.frames.length
        );
        const frame = animDef.frames[frameIndex];
        effect.currentFrame = frame ?? 0;
      }

      // Handle screen shake
      if (effect.type === EffectType.SCREEN_SHAKE && effect.data) {
        const progress = effect.elapsed / effect.data.shakeDuration;
        if (progress < 1) {
          const intensity = effect.data.intensity * (1 - progress);
          this.screenShakeOffset = {
            x: (Math.random() - 0.5) * intensity,
            y: (Math.random() - 0.5) * intensity,
          };
        } else {
          this.screenShakeOffset = { x: 0, y: 0 };
        }
      }

      // Check if effect is complete
      if (effect.elapsed >= effect.duration) {
        if (effect.onComplete) {
          effect.onComplete();
        }
        this.activeEffects.splice(i, 1);
      }
    }
  }

  /**
   * Render all active effects
   */
  render(renderer: Renderer, ctx: CanvasRenderingContext2D): void {
    // Render particles first (behind most effects)
    this.particleSystem.render(ctx);

    // Render effects
    for (const effect of this.activeEffects) {
      this.renderEffect(effect, renderer, ctx);
    }
  }

  private renderEffect(
    effect: BattleEffect,
    renderer: Renderer,
    ctx: CanvasRenderingContext2D
  ): void {
    switch (effect.type) {
      case EffectType.DAMAGE_NUMBER:
        this.renderDamageNumber(effect as DamageNumberEffect, renderer);
        break;

      case EffectType.DAMAGE_FLASH:
        this.renderDamageFlash(effect, renderer);
        break;

      case EffectType.SLASH:
        this.renderSlash(effect, renderer, ctx);
        break;

      case EffectType.FIRE:
        this.renderFire(effect, renderer, ctx);
        break;

      case EffectType.ICE:
        this.renderIce(effect, renderer, ctx);
        break;

      case EffectType.LIGHTNING:
        this.renderLightning(effect, renderer, ctx);
        break;

      case EffectType.HEAL:
        this.renderHeal(effect, renderer, ctx);
        break;

      case EffectType.DEATH:
        this.renderDeath(effect, renderer, ctx);
        break;

      case EffectType.SLEEP:
        this.renderSleep(effect, renderer, ctx);
        break;

      case EffectType.CRITICAL:
        this.renderCritical(effect, renderer, ctx);
        break;

      case EffectType.MISS:
        this.renderMiss(effect, renderer, ctx);
        break;

      case EffectType.ENEMY_SHAKE:
        this.renderEnemyShake(effect, renderer, ctx);
        break;

      default:
        this.renderGenericEffect(effect, renderer);
    }
  }

  private renderDamageNumber(effect: DamageNumberEffect, renderer: Renderer): void {
    const { value, isCrit, isHeal, startY, floatDistance } = effect.data;
    const progress = effect.elapsed / effect.duration;

    // Float up with easing
    const easeOut = 1 - Math.pow(1 - progress, 3);
    const currentY = startY - floatDistance * easeOut;

    // Fade out in last 30%
    const alpha = progress > 0.7 ? 1 - (progress - 0.7) / 0.3 : 1;

    // Choose color
    let color: string;
    if (isHeal) {
      color = '#00ff00';
    } else if (isCrit) {
      color = '#ffff00';
    } else {
      color = '#ffffff';
    }

    // Scale for crits
    const fontSize = isCrit ? 12 : 10;

    renderer.drawText(value.toString(), effect.x, currentY, {
      font: 'ff1',
      fontSize,
      color,
      align: 'center',
      shadow: true,
      shadowColor: `rgba(0, 0, 0, ${alpha})`,
      shadowOffsetX: 1,
      shadowOffsetY: 1,
    });
  }

  private renderDamageFlash(effect: BattleEffect, renderer: Renderer): void {
    const { width, height } = effect.data || { width: 32, height: 32 };
    const progress = effect.elapsed / effect.duration;

    // Flash white then fade
    const alpha = 1 - progress;

    renderer.drawRect(
      effect.x,
      effect.y,
      width,
      height,
      `rgba(255, 255, 255, ${alpha})`,
      true
    );
  }

  private renderSlash(effect: BattleEffect, renderer: Renderer, ctx: CanvasRenderingContext2D): void {
    // Try to get sprite first
    const sprite = this.spriteManager.getSprite('effects', effect.currentFrame);

    if (sprite) {
      renderer.drawSprite(sprite, effect.x - 8, effect.y - 8);
    } else {
      // Enhanced animated slash with glow
      const progress = effect.elapsed / effect.duration;

      ctx.save();

      // Glow effect
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 8;
      ctx.globalAlpha = 1 - progress;

      const size = 24 * Math.min(progress * 2, 1); // Quick extend

      // Main diagonal slash
      ctx.beginPath();
      ctx.moveTo(effect.x - size, effect.y - size);
      ctx.lineTo(effect.x + size, effect.y + size);
      ctx.stroke();

      // Sharp white line on top
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Add sparkles along slash
      if (progress < 0.5) {
        for (let i = 0; i < 3; i++) {
          const t = i / 3;
          const sx = effect.x + (t - 0.5) * size * 2;
          const sy = effect.y + (t - 0.5) * size * 2;
          const sparkSize = 2 * (1 - progress * 2);
          ctx.fillStyle = '#ffff88';
          ctx.fillRect(sx - sparkSize, sy - sparkSize, sparkSize * 2, sparkSize * 2);
        }
      }

      ctx.restore();
    }
  }

  private renderFire(effect: BattleEffect, renderer: Renderer, ctx: CanvasRenderingContext2D): void {
    const sprite = this.spriteManager.getSprite('effects', effect.currentFrame);

    if (sprite) {
      renderer.drawSprite(sprite, effect.x - 8, effect.y - 8);
    } else {
      // Enhanced fire with particles
      const progress = effect.elapsed / effect.duration;

      // Emit flame particles during effect
      if (progress < 0.7 && effect.elapsed % 50 < 20) {
        this.particleSystem.emitFlames(effect.x, effect.y + 10, 2);
      }

      // Rising flames
      const flameCount = 5;
      ctx.save();

      for (let i = 0; i < flameCount; i++) {
        const angle = (i / flameCount) * Math.PI * 2 + progress * 3;
        const radius = 12 * Math.sin(progress * Math.PI); // Expand then contract
        const px = effect.x + Math.cos(angle) * radius;
        const py = effect.y - progress * 30;

        const size = 8 * Math.sin(progress * Math.PI);
        const gradient = ctx.createRadialGradient(px, py, 0, px, py, size);
        gradient.addColorStop(0, '#ffff00');
        gradient.addColorStop(0.4, '#ff6600');
        gradient.addColorStop(1, 'rgba(255, 0, 0, 0)');

        ctx.fillStyle = gradient;
        ctx.fillRect(px - size, py - size, size * 2, size * 2);
      }

      ctx.restore();
    }
  }

  private renderIce(effect: BattleEffect, renderer: Renderer, ctx: CanvasRenderingContext2D): void {
    const sprite = this.spriteManager.getSprite('effects', effect.currentFrame);

    if (sprite) {
      renderer.drawSprite(sprite, effect.x - 8, effect.y - 8);
    } else {
      // Enhanced ice with shatter effect
      const progress = effect.elapsed / effect.duration;

      // Emit ice shards at start
      if (progress < 0.2 && effect.elapsed < 50) {
        this.particleSystem.emitIceShards(effect.x, effect.y, 10);
      }

      ctx.save();
      ctx.fillStyle = `rgba(150, 200, 255, ${1 - progress})`;
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1;

      // Expanding crystalline pattern
      const crystalCount = 8;
      for (let i = 0; i < crystalCount; i++) {
        const angle = (i / crystalCount) * Math.PI * 2 + progress * 0.5;
        const dist = progress < 0.5 ? progress * 40 : 20 + (progress - 0.5) * 30; // Burst out
        const cx = effect.x + Math.cos(angle) * dist;
        const cy = effect.y + Math.sin(angle) * dist;
        const size = 5 * (1 - progress);

        // Crystal shard shape
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(angle);
        ctx.beginPath();
        ctx.moveTo(0, -size * 1.5);
        ctx.lineTo(size * 0.5, 0);
        ctx.lineTo(0, size * 0.5);
        ctx.lineTo(-size * 0.5, 0);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      }

      ctx.restore();
    }
  }

  private renderLightning(effect: BattleEffect, renderer: Renderer, ctx: CanvasRenderingContext2D): void {
    const sprite = this.spriteManager.getSprite('effects', effect.currentFrame);

    if (sprite) {
      renderer.drawSprite(sprite, effect.x - 8, effect.y - 8);
    } else {
      // Enhanced lightning with multiple bolts and sparks
      const progress = effect.elapsed / effect.duration;

      // Emit sparks at start
      if (progress < 0.3 && effect.elapsed % 40 < 20) {
        this.particleSystem.emitLightningSparks(effect.x, effect.y, 3);
      }

      if (progress < 0.8) {
        ctx.save();
        const flicker = Math.random() > 0.4 ? 1 : 0.6;
        ctx.globalAlpha = flicker * (1 - progress / 0.8);

        // Main bolt
        this.drawLightningBolt(ctx, effect.x, effect.y - 50, effect.x, effect.y + 20, 6, '#ffff00');

        // Secondary bolts
        if (Math.random() > 0.5) {
          this.drawLightningBolt(
            ctx,
            effect.x - 10,
            effect.y - 30,
            effect.x + 15,
            effect.y + 10,
            4,
            '#aaffff'
          );
        }

        ctx.restore();
      }
    }
  }

  private drawLightningBolt(
    ctx: CanvasRenderingContext2D,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    segments: number,
    color: string
  ): void {
    // Draw jagged lightning bolt
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x1, y1);

    const dx = x2 - x1;
    const dy = y2 - y1;

    for (let i = 1; i < segments; i++) {
      const t = i / segments;
      const x = x1 + dx * t + (Math.random() - 0.5) * 15;
      const y = y1 + dy * t;
      ctx.lineTo(x, y);
    }

    ctx.lineTo(x2, y2);
    ctx.stroke();

    // Glow
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.lineWidth = 6;
    ctx.stroke();
  }

  private renderHeal(effect: BattleEffect, renderer: Renderer, ctx: CanvasRenderingContext2D): void {
    const sprite = this.spriteManager.getSprite('effects', effect.currentFrame);

    if (sprite) {
      renderer.drawSprite(sprite, effect.x - 8, effect.y - 8);
    } else {
      // Enhanced heal with particles
      const progress = effect.elapsed / effect.duration;

      // Emit healing sparkles continuously
      if (progress < 0.8 && effect.elapsed % 80 < 20) {
        this.particleSystem.emitHealSparkles(effect.x, effect.y, 2);
      }

      // Pulsing green glow
      ctx.save();
      const pulseSize = 20 + Math.sin(progress * Math.PI * 4) * 5;
      const alpha = (1 - progress) * 0.3;

      const gradient = ctx.createRadialGradient(effect.x, effect.y, 0, effect.x, effect.y, pulseSize);
      gradient.addColorStop(0, `rgba(100, 255, 100, ${alpha})`);
      gradient.addColorStop(1, 'rgba(100, 255, 100, 0)');

      ctx.fillStyle = gradient;
      ctx.fillRect(effect.x - pulseSize, effect.y - pulseSize, pulseSize * 2, pulseSize * 2);

      ctx.restore();
    }
  }

  private renderDeath(effect: BattleEffect, renderer: Renderer, ctx: CanvasRenderingContext2D): void {
    const sprite = this.spriteManager.getSprite('effects', effect.currentFrame);

    if (sprite) {
      renderer.drawSprite(sprite, effect.x - 8, effect.y - 8);
    } else {
      // Enhanced death animation: fade to darkness
      const progress = effect.elapsed / effect.duration;

      ctx.save();

      // Expanding dark rings
      for (let i = 0; i < 3; i++) {
        const ringProgress = (progress + i * 0.2) % 1;
        const alpha = Math.sin(ringProgress * Math.PI) * 0.5;
        ctx.strokeStyle = `rgba(80, 0, 80, ${alpha})`;
        ctx.lineWidth = 3;

        const radius = ringProgress * 35;
        ctx.beginPath();
        ctx.arc(effect.x, effect.y, radius, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Dark vignette
      if (progress > 0.5) {
        const vignetteAlpha = (progress - 0.5) * 0.4;
        const gradient = ctx.createRadialGradient(
          effect.x,
          effect.y,
          10,
          effect.x,
          effect.y,
          50
        );
        gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
        gradient.addColorStop(1, `rgba(0, 0, 0, ${vignetteAlpha})`);
        ctx.fillStyle = gradient;
        ctx.fillRect(effect.x - 50, effect.y - 50, 100, 100);
      }

      ctx.restore();
    }
  }

  private renderSleep(effect: BattleEffect, _renderer: Renderer, ctx: CanvasRenderingContext2D): void {
    const progress = effect.elapsed / effect.duration;
    const bounce = Math.sin(progress * Math.PI * 2) * 3;

    // Draw "Zzz" floating upward
    ctx.save();
    ctx.fillStyle = `rgba(255, 255, 255, ${1 - progress})`;
    ctx.font = '12px monospace';

    const zCount = 3;
    for (let i = 0; i < zCount; i++) {
      const offset = i * 8;
      const y = effect.y - progress * 20 - offset + bounce;
      const size = 12 - i * 2;
      ctx.font = `${size}px monospace`;
      ctx.fillText('Z', effect.x + offset, y);
    }

    ctx.restore();
  }

  private renderCritical(effect: BattleEffect, _renderer: Renderer, ctx: CanvasRenderingContext2D): void {
    const progress = effect.elapsed / effect.duration;

    // Burst of sparkles
    if (progress < 0.3) {
      this.particleSystem.emitSparkles(effect.x, effect.y, 15, '#ffff00');
    }

    // "CRITICAL!" text
    if (progress < 0.8) {
      ctx.save();
      ctx.fillStyle = `rgba(255, 255, 0, ${1 - progress / 0.8})`;
      ctx.font = 'bold 10px monospace';
      ctx.textAlign = 'center';
      const scale = 1 + Math.sin(progress * Math.PI) * 0.3;
      ctx.save();
      ctx.translate(effect.x, effect.y);
      ctx.scale(scale, scale);
      ctx.fillText('CRITICAL!', 0, 0);
      ctx.restore();
      ctx.restore();
    }
  }

  private renderMiss(effect: BattleEffect, _renderer: Renderer, ctx: CanvasRenderingContext2D): void {
    const progress = effect.elapsed / effect.duration;

    // "MISS" text floating up
    ctx.save();
    const y = effect.y - progress * 15;
    const alpha = 1 - progress;
    ctx.fillStyle = `rgba(150, 150, 150, ${alpha})`;
    ctx.font = '10px monospace';
    ctx.textAlign = 'center';
    ctx.fillText('MISS', effect.x, y);
    ctx.restore();
  }

  private renderEnemyShake(_effect: BattleEffect, _renderer: Renderer, _ctx: CanvasRenderingContext2D): void {
    // This effect is handled by modifying enemy position in BattleScene
    // No rendering needed here
  }

  private renderGenericEffect(effect: BattleEffect, renderer: Renderer): void {
    // Generic sprite-based effect
    const sprite = this.spriteManager.getSprite('effects', effect.currentFrame);

    if (sprite) {
      renderer.drawSprite(sprite, effect.x - 8, effect.y - 8);
    } else {
      // Fallback: simple expanding circle
      const progress = effect.elapsed / effect.duration;
      renderer.drawRect(
        effect.x - 4,
        effect.y - 4,
        8,
        8,
        `rgba(255, 255, 255, ${1 - progress})`,
        true
      );
    }
  }

  private getEffectDuration(type: EffectType): number {
    const durations: Record<EffectType, number> = {
      [EffectType.SLASH]: 400,
      [EffectType.FIRE]: 700,
      [EffectType.ICE]: 700,
      [EffectType.LIGHTNING]: 500,
      [EffectType.HEAL]: 1000,
      [EffectType.CRITICAL]: 600,
      [EffectType.MISS]: 600,
      [EffectType.POISON]: 1200,
      [EffectType.DEATH]: 800,
      [EffectType.DAMAGE_FLASH]: 200,
      [EffectType.DAMAGE_NUMBER]: 1000,
      [EffectType.SCREEN_SHAKE]: 200,
      [EffectType.VICTORY_POSE]: 2000,
      [EffectType.SLEEP]: 1500,
      [EffectType.CHARACTER_STEP]: 300,
      [EffectType.ENEMY_SHAKE]: 200,
    };

    return durations[type] ?? 500;
  }

  /**
   * Clear all active effects
   */
  clear(): void {
    this.activeEffects = [];
    this.particleSystem.clear();
    this.screenShakeOffset = { x: 0, y: 0 };
  }

  /**
   * Check if any effects are currently playing
   */
  hasActiveEffects(): boolean {
    return this.activeEffects.length > 0 || this.particleSystem.hasParticles();
  }

  /**
   * Get particle system for direct access
   */
  getParticleSystem(): ParticleSystem {
    return this.particleSystem;
  }
}
